import { useEffect, useState, type ReactNode } from "react";
import { AxonMark } from "../../design-system/primitives/AxonMark";
import { Icon } from "../../design-system/primitives/Icon";
import { Modal } from "../../design-system/primitives/Modal";
import { cn } from "../../design-system/tokens";
import { FeedbackButton } from "../feedback/FeedbackButton";
import { APP_VERSION_LABEL } from "../../lib/appVersion";
import { useLangStore } from "../../store/langStore";
import { t as tr, type Lang } from "../../lib/i18n";

const AXON_GROUP_URL = "https://axongroup.com.co/";
const AXON_GROUP_LOGO_URL = `${import.meta.env.BASE_URL}axon-group-logo.svg`;

const SUPPORTED_LANGUAGES: Lang[] = ["es", "en"];

/** Flag en localStorage que marca el dialogo de bienvenida como visto. */
const WELCOME_SEEN_KEY = "axon.tags.welcome.seen";

function hasSeenWelcome(): boolean {
  try {
    return window.localStorage.getItem(WELCOME_SEEN_KEY) !== null;
  } catch {
    return true;
  }
}

function markWelcomeSeen(): void {
  try {
    window.localStorage.setItem(WELCOME_SEEN_KEY, new Date().toISOString());
  } catch {
    // Ignorar: mejor esfuerzo.
  }
}

const CAPABILITIES = [
  "IEC 61850 / MMS",
  "SIEMENS SIPROTEC 5",
  "ABB / Hitachi",
  "SEL",
  "Excel / CSV",
  "SCL (.scd / .cid)",
];

// Textos largos en español (las versiones en inglés viven en el diccionario).
const DESCRIPTION =
  "Asigna y valida nodos lógicos MMS IEC 61850 a partir de listados de señales (Excel/CSV) y archivos SCL (.scd/.cid), con clasificación asistida por IA que corre por completo en tu navegador.";
const GROUP =
  "En Axon Group Ltda desarrollamos software y proveemos servicios de ingeniería para la automatización de subestaciones y sistemas SCADA del sector eléctrico. Visítanos y conoce más axongroup.com.co.";
const PRIVACY =
  "Tus señales y archivos SCL se procesan en esta pestaña y nunca se envían a un servidor. Al cerrarla, los datos se descartan.";

/** Parte un texto en torno a `token` y envuelve cada aparición con `wrap`. */
function splitToken(
  text: string,
  token: string,
  wrap: (tk: string, key: number) => ReactNode,
): ReactNode[] {
  const parts = text.split(token);
  const out: ReactNode[] = [];
  parts.forEach((p, i) => {
    out.push(p);
    if (i < parts.length - 1) out.push(wrap(token, i));
  });
  return out;
}

export function AboutButton() {
  const { lang } = useLangStore();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenWelcome()) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    markWelcomeSeen();
  };

  return (
    <>
      <FeedbackButton />
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={tr("Información de la aplicación", lang)}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-on-surface-variant)] transition-colors hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)]"
      >
        <Icon name="info" size={18} />
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        ariaLabel={tr("Información de Axon Tags", lang)}
        widthClass="w-[min(460px,92vw)]"
      >
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center gap-3 pr-8">
            <AxonMark size={36} />
            <div className="flex flex-col gap-0.5">
              <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
                Axon Tags
              </h2>
              <span className="font-mono text-[10px] text-[var(--color-on-surface-variant)]">
                {APP_VERSION_LABEL}
              </span>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
            {splitToken(tr(DESCRIPTION, lang), "IEC 61850", (tk, k) => (
              <span key={k} className="font-mono text-[var(--color-brand)]">
                {tk}
              </span>
            ))}
          </p>

          <Section title={tr("Capacidades", lang)}>
            <div className="flex flex-wrap gap-1.5">
              {CAPABILITIES.map((label) => (
                <CapabilityChip key={label} label={label} />
              ))}
            </div>
          </Section>

          <Section title={tr("Privacidad", lang)}>
            <p className="text-sm text-[var(--color-on-surface-variant)]">
              {tr(PRIVACY, lang)}
            </p>
          </Section>

          <Section title={tr("Idioma", lang)}>
            <LanguageSwitcher />
          </Section>

          <Section title={tr("Sobre Axon Group", lang)}>
            <img
              src={AXON_GROUP_LOGO_URL}
              alt="Axon Group"
              className="h-8 w-auto select-none"
              draggable={false}
            />
            <p className="text-sm text-[var(--color-on-surface-variant)]">
              {splitToken(tr(GROUP, lang), "axongroup.com.co", (tk, k) => (
                <a
                  key={k}
                  href={AXON_GROUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-brand)] hover:underline"
                >
                  {tk}
                </a>
              ))}
            </p>
          </Section>

          <div className="flex flex-col gap-2 pt-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <a
                href={AXON_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-brand-border-30)] px-4 py-2 text-sm font-medium text-[var(--color-brand)] transition-colors hover:bg-[var(--color-brand-soft-10)]"
              >
                {tr("Conoce Axon Group", lang)}
                <Icon name="open_in_new" size={14} />
              </a>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-black transition-opacity hover:opacity-90"
              >
                {tr("Comenzar", lang)}
              </button>
            </div>
            <span className="text-xs text-[var(--color-on-surface-variant)]/70">
              © {new Date().getFullYear()} Axon Group Ltda ·{" "}
              <a
                href={AXON_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-brand)] hover:underline"
              >
                axongroup.com.co
              </a>
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Selector de idioma — dos chips ES / EN que actualizan el langStore. */
function LanguageSwitcher() {
  const { lang, setLang } = useLangStore();
  return (
    <div className="flex gap-1.5">
      {SUPPORTED_LANGUAGES.map((code) => {
        const isActive = code === lang;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            aria-pressed={isActive}
            className={cn(
              "inline-flex h-7 items-center rounded border px-3 font-mono text-[11px] uppercase tracking-wider transition-colors focus:outline-none",
              isActive
                ? "border-[var(--color-brand-border-60)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)]"
                : "border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] text-[var(--color-on-surface-variant)] hover:border-[var(--color-border)] hover:text-[var(--color-on-surface)]",
            )}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function CapabilityChip({ label }: { label: string }) {
  return (
    <span className="inline-flex h-6 items-center gap-1.5 rounded border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)] px-2 font-mono text-[10px] text-[var(--color-on-surface-variant)]">
      <Icon name="check" size={12} className="text-[var(--color-brand)]/80" />
      <span>{label}</span>
    </span>
  );
}
